import { toast } from 'vue3-toastify';

import { useDefaultRecordsPerPage } from '@/composables/useDefaultRecordsPerPage';
import type {
  IPagePermission,
  IPages,
  IProfileArea,
  IRolePermission,
  IRolePermissionPayload,
  IUserManagementProfile,
  IUserManagementResponse,
  IUserPermissionPayload,
  IUserProfile
} from '@/interfaces/agency/settings/user-management/users';
import { isPowerRole } from '@/router/guards/permission.guard';
import userManagementServices from '@/services/agency/settings/user-management';
import { useAuthStore } from '@/stores/agency/auth';

const { getDefaultRecordsPerPageFromStorage } = useDefaultRecordsPerPage();

export const useUserManagementStore = defineStore('userManagementStore', {
  state: () => ({
    isUsersLoading: false,
    userManagementProfilesResponse: {} as IUserManagementResponse,
    userManagementProfilesById: {} as IUserProfile,
    pagePermissions: [] as IPages[],
    isPagePermissionLoading: false as boolean,
    rolesPermissions: [] as IRolePermission[],
    isRolesPermissionsLoading: false as boolean,
    rolesPermissionById: [] as IPages[],
    isRolePermissionsByIdLoading: false as boolean,
    usersPermissionById: [] as IPages[],
    permissionCache: new Map<string, boolean>(),
    query: {
      page: 1,
      limit: getDefaultRecordsPerPageFromStorage(),
      sortBy: null as string | null,
      search: null as string | null,
      roleId: [1, 2, 3, 7, 8],
      statusId: null as number | null,
      hasRolePermissions: null as boolean | null
    }
  }),
  getters: {
    getUsersList: state => {
      return state.userManagementProfilesResponse.data as IUserProfile[];
    },

    getUserById: state => {
      return state.userManagementProfilesById as IUserProfile;
    },

    getPageMeta: state => {
      return {
        totalItems: state.userManagementProfilesResponse.pagination?.totalItems,
        totalPages: state.userManagementProfilesResponse.pagination?.totalPages
      };
    },

    getPagePermission: state => {
      return state.pagePermissions as IPages[];
    },

    getRolesPermissions: state => {
      return state.rolesPermissions as IRolePermission[];
    },

    getRolesPermissionById: state => {
      return state.rolesPermissionById as IPages[];
    },

    getUserPermissionById: state => {
      return state.usersPermissionById as IPages[];
    }
  },
  actions: {
    async fetchUsers() {
      this.isUsersLoading = true;
      try {
        const { data } = await userManagementServices.fetchUsers(this.query);

        this.userManagementProfilesResponse = data;
      } finally {
        this.isUsersLoading = false;
      }
    },

    async fetchUserById(id: number) {
      const { data } = await userManagementServices.fetchUserById(id);

      if (data.profileAreas && Array.isArray(data.profileAreas)) {
        data.areaIds = data.profileAreas.map((profileArea: IProfileArea) => profileArea.area.id);
      } else {
        data.areaIds = [];
      }

      this.userManagementProfilesById = data;
    },

    async addUsers(payload: IUserManagementProfile, options?: { silent?: boolean }) {
      const { data } = await userManagementServices.addUser(payload);

      this.fetchUsers();

      if (!options?.silent) {
        toast.success('User created successfully');
      }

      this.updateUserTenantRoles(payload.email);

      return data.data;
    },

    async editUsers(profileId: number, payload: IUserManagementProfile, options?: { silent?: boolean }) {
      const { data } = await userManagementServices.updateUser(profileId, payload);

      this.fetchUsers();
      if (!options?.silent) {
        toast.success('User updated successfully');
      }

      return data;
    },

    async removeUser(profileId: number) {
      await userManagementServices.removeUser(profileId);

      this.fetchUsers();
      toast.success('User Deleted Successfully');

      const userEmail = this.userManagementProfilesResponse?.data?.find(user => user.id === profileId)?.email;

      this.updateUserTenantRoles(userEmail ?? '');
    },

    updateUserTenantRoles(userEmail: string) {
      const tenantData = JSON.parse(localStorage.getItem('userData') || '{}');
      const tenantEmail = tenantData?.email ?? '';

      if (tenantEmail === userEmail) {
        const authStore = useAuthStore();

        const { id: userId } = authStore.getUserCDetails;

        authStore.fetchUserTenantRoles(Number(userId));
      }
    },

    async fetchPagePermission(hideLoader = false) {
      this.isPagePermissionLoading = true;

      try {
        const { data } = await userManagementServices.fetchPagePermission(hideLoader);

        this.pagePermissions = data;
      } finally {
        this.isPagePermissionLoading = false;
      }
    },

    async fetchRolesPermissions(hideLoader = false) {
      this.isRolesPermissionsLoading = true;

      try {
        const { data } = await userManagementServices.fetchRolesPermissions(hideLoader);

        this.rolesPermissions = data;
      } finally {
        this.isRolesPermissionsLoading = false;
      }
    },

    async addRolesPermissions(payload: IRolePermissionPayload) {
      await userManagementServices.addRolesPermissions(payload);

      this.fetchRolesPermissions();
      toast.success('Role Permission created successfully');
    },

    async editRolesPermissions(payload: IRolePermissionPayload) {
      await userManagementServices.editRolesPermissions(payload);

      this.fetchRolesPermissions();
      toast.success('Role Permission updated successfully');
    },

    async fetchRolesPermissionsById(roleId: number, hideLoader = false) {
      this.isRolePermissionsByIdLoading = true;

      try {
        const { data } = await userManagementServices.fetchRolesPermissionsById(roleId, hideLoader);

        this.rolesPermissionById = data;
      } finally {
        this.isRolePermissionsByIdLoading = false;
      }
    },

    async addUsersPermissions(payload: IUserPermissionPayload, options?: { silent?: boolean }) {
      await userManagementServices.addUsersPermissions(payload);

      this.fetchUsers();
      if (!options?.silent) {
        toast.success('Users Permission created successfully');
      }
    },

    async editUsersPermissions(payload: IUserPermissionPayload, options?: { silent?: boolean }) {
      await userManagementServices.editUsersPermissions(payload);

      this.fetchUsers();
      if (!options?.silent) {
        toast.success('Users Permission updated successfully');
      }
    },

    async fetchUsersPermissionsById(profileId: number) {
      const { data } = await userManagementServices.fetchUsersPermissionsById(profileId);

      localStorage.setItem('userPermissions', JSON.stringify(data));

      this.usersPermissionById = data;

      this.permissionCache.clear();
    },

    async changeUserPassword(profileId: number, payload: { newPassword: string; confirmPassword: string }) {
      await userManagementServices.changeUserPassword(profileId, payload);

      toast.success('Password changed successfully');
    },

    async rolesAreaRestricted(roleId: number, payload: { isAreaRestricted: boolean }) {
      await userManagementServices.rolesAreaRestricted(roleId, payload);

      const message = payload.isAreaRestricted
        ? 'Role area restricted enabled successfully'
        : 'Role area restricted disabled successfully';

      toast.success(message);
    },

    hasPermissionInPages(pages: IPages[], key: string): boolean {
      for (const page of pages) {
        if (page.permissions?.some(p => p.key === key && p.isEnabled)) {
          return true;
        }
        if (page.childPages?.length && this.hasPermissionInPages(page.childPages, key)) {
          return true;
        }
      }

      return false;
    },

    userHasPermission(key: string): boolean {
      if (this.permissionCache.has(key)) {
        return this.permissionCache.get(key)!;
      }

      // Owner-viewer impersonation sessions have full access
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      if (userData?.isOwnerViewer) {
        this.permissionCache.set(key, true);

        return true;
      }

      const authStore = useAuthStore();
      const roleSlug = authStore.loggedInUserProfile?.role?.slug;

      if (isPowerRole(roleSlug)) {
        this.permissionCache.set(key, true);

        return true;
      }

      const hasPermission = this.hasPermissionInPages(this.usersPermissionById, key);

      this.permissionCache.set(key, hasPermission);

      return hasPermission;
    },

    resetQuery() {
      this.query = {
        search: this.query.search,
        page: 1,
        limit: getDefaultRecordsPerPageFromStorage(),
        sortBy: null,
        roleId: [1, 2, 3, 7, 8],
        statusId: null,
        hasRolePermissions: null
      };
    },

    getFirstAllowedPermissionKey(): string | undefined {
      // Helper to recursively collect all enabled permissions
      const collectEnabledPermissions = (pages: IPages[]): IPagePermission[] => {
        const permissions: IPagePermission[] = [];

        for (const page of pages) {
          // Collect enabled permissions from current page
          if (page.permissions) {
            permissions.push(...page.permissions.filter(p => p.isEnabled));
          }

          // Recursively collect from child pages
          if (page.childPages?.length) {
            permissions.push(...collectEnabledPermissions(page.childPages));
          }
        }

        return permissions;
      };

      const allEnabledPermissions = collectEnabledPermissions(this.usersPermissionById);

      // Prioritize .read permissions for routing (they determine page access)
      const readPermission = allEnabledPermissions.find(p => p.key.endsWith('.access'));
      if (readPermission) {
        return readPermission.key;
      }

      // Fall back to first enabled permission if no .read permission found
      return allEnabledPermissions[0]?.key;
    },

    getFirstAllowedRoute(permissionToRouteMap: Record<string, string>): string {
      const firstKey = this.getFirstAllowedPermissionKey();

      return firstKey && permissionToRouteMap[firstKey] ? permissionToRouteMap[firstKey] : '/not-authorized';
    }
  }
});
